import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChevronDown, ChevronRight, Loader2, RefreshCw, ShieldOff } from "lucide-react";
import { toast } from "@/lib/toast";

/**
 * Library Stats card: who is hidden for consent, and why. Read-only apart
 * from "Re-check Drive permissions now" (that job also runs hourly).
 * Backed by src/server/consent.ts.
 */

const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString() : "not yet");

export default function ConsentVisibilityCard() {
  const summaryQuery = trpc.admin.consentSummary.useQuery(undefined, {
    refetchInterval: (q) => (q.state.data?.driveSync.status === "running" ? 2000 : false),
  });
  const syncMutation = trpc.admin.consentDriveSyncStart.useMutation();
  const s = summaryQuery.data;
  const running = s?.driveSync.status === "running";
  const [showNames, setShowNames] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [seenFinishedAt, setSeenFinishedAt] = useState<number | null>(s?.driveSync.finishedAt ?? null);

  useEffect(() => {
    const d = s?.driveSync;
    if (!d || d.status === "running" || d.status === "idle" || d.finishedAt === seenFinishedAt) return;
    setSeenFinishedAt(d.finishedAt);
    if (d.status === "error") toast.error(`Drive permission check failed: ${d.error ?? "unknown error"}`);
  }, [s, seenFinishedAt]);

  const start = async () => {
    try {
      const r = await syncMutation.mutateAsync();
      if (!r.started) toast.info("A Drive permission check is already running.");
      void summaryQuery.refetch();
    } catch (err: any) {
      toast.error(`Couldn't start: ${err?.message ?? "unknown error"}`);
    }
  };

  const hiddenClips = (s?.excludedPatients ?? []).reduce((n, p) => n + p.hiddenClips, 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <ShieldOff className="w-4 h-4" />
          Consent visibility
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Footage that&apos;s hidden everywhere (search, Recents, overviews, playback, downloads) because a
          patient&apos;s consent changed after upload. It updates on its own, and anything that moves back to an
          eligible folder reappears.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 text-xs">
        {/* NAS Private + Home Use Only */}
        <div className="p-3 rounded border border-border space-y-1">
          <div className="text-sm font-medium">
            Private + Home Use Only (NAS): {s?.excludedPatients.length ?? 0} patient(s), {hiddenClips} clip(s) hidden
          </div>
          <div className="text-muted-foreground">
            Every clip for a patient whose NAS folder is under Private + Home Use Only, Drive copies included. The
            scanner only reads the patient folder names there, never the files. Last report from the NAS:{" "}
            {when(s?.lastNasReport)}.
          </div>
          {(s?.excludedPatients.length ?? 0) > 0 && (
            <>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-primary hover:underline"
                onClick={() => setShowNames((v) => !v)}
              >
                {showNames ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                {showNames ? "Hide" : "Show"} patients
              </button>
              {showNames && (
                <div className="max-h-48 overflow-auto rounded bg-muted/30 p-2 font-mono text-[11px] space-y-0.5">
                  {s!.excludedPatients.map((p) => (
                    <div key={`${p.location}|${p.name}`}>
                      {p.name} ({p.location}): {p.hiddenClips} clip(s), since {new Date(p.since).toLocaleDateString()}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* NAS presence */}
        <div className="p-3 rounded border border-border space-y-1">
          <div className="text-sm font-medium">Left their NAS folder: {s?.nasMissingHidden ?? 0} clip(s) hidden</div>
          <div className="text-muted-foreground">
            NAS-only clips that are no longer where the app recorded them, e.g. moved into a Private folder or
            deleted. Clips with a Google Drive copy follow Drive instead. Last check: {when(s?.lastPresenceCheck)}.
          </div>
        </div>

        {/* Drive permission re-sync */}
        <div className="flex items-start gap-3 p-3 rounded border border-border">
          <div className="flex-1 space-y-1">
            <div className="text-sm font-medium">Drive permission check</div>
            <div className="text-muted-foreground">
              Re-reads which permission folder each patient&apos;s Drive folder is in now, and updates their footage
              to match. A patient moved to Private + Internal Use Only drops out of search. Runs every hour. Last
              run: {when(s?.lastDriveSync)}.
            </div>
            {s?.driveSync.status === "done" && s.driveSync.startedAt && (
              <div>
                Latest run: {s.driveSync.patientsChecked} patient(s) checked, {s.driveSync.changes.length} changed
                ({s.driveSync.rowsUpdated} clip(s) updated).
                {s.driveSync.changes.length > 0 && (
                  <>
                    {" "}
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-primary hover:underline"
                      onClick={() => setShowChanges((v) => !v)}
                    >
                      {showChanges ? "Hide" : "Show"} changes
                    </button>
                    {showChanges && (
                      <div className="mt-1 max-h-48 overflow-auto rounded bg-muted/30 p-2 font-mono text-[11px] space-y-0.5">
                        {s.driveSync.changes.map((c) => (
                          <div key={c}>{c}</div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {running && (
              <div>
                Checking… {s?.driveSync.patientsChecked ?? 0} patient(s) so far
              </div>
            )}
          </div>
          <button
            type="button"
            disabled={running || syncMutation.isPending}
            onClick={() => void start()}
            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded border border-border bg-background hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Check now
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
